import React, { useState } from 'react';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import EmptyCard from './subcomponents/EmptyCard';
import NumbersTable from './subcomponents/NumbersTable';
import AddNumbersDialog from './subcomponents/AddNumbersDialog';
import UploadNumbersFromFileDialog from 'components/UploadFileDialog/UploadFileDialog';
import { translate } from 'components/Translation';
import Papa from 'papaparse';
import DuplicatedNumbersWarningDialog from './subcomponents/DuplicatedNumbersWarningDialog';
import { of, zip } from 'rxjs';
import { NumbersService } from 'services/numbers/numbers';
import { catchError, map } from 'rxjs/operators';
import UploadNumbersErrorDialog from './subcomponents/UploadNumbersErrorDialog';
import { BillingType } from 'utils/api/models/accounts/singleAccount';

interface IAddNumbersTab {
	numbers: string[];
	addNumbers: (numbers: string[]) => void;
	removeNumbers: (numbers: string[]) => void;
	buttons: types.prepaidLoyalty.IButton[];
}

const dialogs = {
	NONE: 'none',
	ADD_NUMBERS_DIALOG: 'addNumbersDialog',
	UPLOAD_NUMBERS_FROM_FILE_DIALOG: 'uploadNumbersFromFileDialog',
	UPLOAD_NUMBERS_FROM_FILE_ERROR_DIALOG: 'uploadNumbersFromFileErrorDialog',
	DUPLICATED_NUMBERS_WARNING: 'duplicatedNumbersWarning',
};

const loaders = {
	NONE: 0,
	UPLOADING_CSV: 1,
};

const SAMPLE_CSV_URL = '/templates/NumbersTemplate.csv';

const AddNumbersTab = ({ numbers, addNumbers, buttons, removeNumbers }: IAddNumbersTab) => {
	const [openDialog, setOpenDialog] = useState<string>(dialogs.NONE);
	const [loading, setLoading] = useState<number>(loaders.NONE);

	const showAddNumbersDialog = () => {
		setOpenDialog(dialogs.ADD_NUMBERS_DIALOG);
	};

	const showUploadNumbersFromFileDialog = () => {
		setOpenDialog(dialogs.UPLOAD_NUMBERS_FROM_FILE_DIALOG);
	};

	const hideDialog = () => {
		setOpenDialog(dialogs.NONE);
	};

	const addNumbersAndCheckForDuplicates = (newNumbers: string[]) => {
		var deduplicatedNewNumbers = newNumbers.filter((newNumber) => numbers.indexOf(newNumber) < 0);
		addNumbers(deduplicatedNewNumbers);
		if (newNumbers.length > deduplicatedNewNumbers.length) {
			setOpenDialog(dialogs.DUPLICATED_NUMBERS_WARNING);
		}
	};

	const onUploadNumbersError = () => {
		setOpenDialog(dialogs.UPLOAD_NUMBERS_FROM_FILE_ERROR_DIALOG);
	};

	const onFileChange = (base64data: string) => {
		setLoading(loaders.UPLOADING_CSV);
		fetch(base64data)
			.then((res) => res.blob())
			.then((blob) => {
				const file = new File([blob], 'newNumbers', { type: 'text/csv' });
				Papa.parse(file, {
					header: false,
					skipEmptyLines: true,
					complete: function(results) {
						zip(
							...(results?.data?.flat() || []).map((number) => {
								return NumbersService.getAccountNumberInfo(number as string)
									.pipe(catchError(() => of(null)))
									.pipe(map((res) => (res?.billingType === BillingType.PREPAID ? res : null)));
							})
						).subscribe((response) => {
							setLoading(loaders.NONE);
							addNumbers(response.filter((n) => n).map((el) => el?.accountNum));
							if (response.filter((el) => !el).length > 0) {
								onUploadNumbersError();
							} else {
								hideDialog();
							}
						});
					},
				});
			});
	};

	return (
		<>
			<ConditionalRender
				show={numbers.length <= 0}
				onTrue={() => (
					<EmptyCard
						showAddNumbersDialog={showAddNumbersDialog}
						showUploadNumbersFromFileDialog={showUploadNumbersFromFileDialog}
					/>
				)}
				onFalse={() => (
					<NumbersTable
						numbers={numbers}
						showAddNumbersDialog={showAddNumbersDialog}
						showUploadNumbersFromFileDialog={showUploadNumbersFromFileDialog}
						buttons={buttons}
						removeNumbers={removeNumbers}
					/>
				)}
			/>
			<AddNumbersDialog
				isOpen={openDialog === dialogs.ADD_NUMBERS_DIALOG}
				hideDialog={hideDialog}
				addNumbers={addNumbersAndCheckForDuplicates}
			/>
			<DuplicatedNumbersWarningDialog
				isOpen={openDialog === dialogs.DUPLICATED_NUMBERS_WARNING}
				hideDialog={hideDialog}
			/>
			<UploadNumbersFromFileDialog
				isOpen={openDialog === dialogs.UPLOAD_NUMBERS_FROM_FILE_DIALOG}
				hideDialog={hideDialog}
				onFileChange={onFileChange}
				uploadingCsv={loading === loaders.UPLOADING_CSV}
				sampleCsvUrl={SAMPLE_CSV_URL}
				header={translate('prepaid.loyalty.import')}
			/>
			<UploadNumbersErrorDialog
				isOpen={openDialog === dialogs.UPLOAD_NUMBERS_FROM_FILE_ERROR_DIALOG}
				hideDialog={hideDialog}
			/>
		</>
	);
};

export default AddNumbersTab;
